import { Component, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, HostListener, input, model, signal, viewChild } from '@angular/core';
import { DialogContainerOptions, DynamicComponentBase, NgDynamicComponentsService } from '../service/ng-dynamic-components.service';
import { Stdlib } from '../lib/stdlib';
import { FormsModule } from '@angular/forms';

const defaultOptions = {
    fontSize: 10,
    automaticLayout: true,
    minimap: {
        enabled: false
    },
};

interface MonacoIframeApi {
    setValue(value: string): void;
    getValue(): string;
    setLanguage(language: string): void;
    setFontSize(fontSize: number): void;
    setLineNumbers(show: boolean): void;
    setTheme(theme: string): void;
    setWordWrap(wordWrap: string): void;
    setMinimap(enabled: boolean): void;
}


@Component({
    selector: 'app-monaco-editor',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        FormsModule
    ],
    templateUrl: './monaco-editor.component.html',
    styleUrl: './monaco-editor.component.scss'
})
export class MonacoEditorComponent extends DynamicComponentBase {

    protected ready = signal(false);

    initialValue = input<string | null>(null);
    language = model<string>('xml');
    lineNos = model<boolean>(true);
    wordWrap = model<boolean>(false);
    darkMode = model<boolean>(false);
    minimap = model<boolean>(false);
    fontSize = model<number>(11);

    private editorIframe = viewChild<ElementRef<HTMLIFrameElement>>('editor');
    private editorApi: MonacoIframeApi | undefined;

    constructor() {
        super();
        effect(() => this.setLanguage(this.language()));
        effect(() => this.setLineNumbers(this.lineNos()));
        effect(() => this.setDark(this.darkMode()));
        effect(() => this.setWordWrap(this.wordWrap()));
        effect(() => this.setMinimap(this.minimap()));
        effect(() => this.setFontSize(this.fontSize()));
    }

    @HostListener('window:message', ['$event'])
    onMessage(event: MessageEvent) {
        if (event.source === this.editorIframe()?.nativeElement?.contentWindow && event.data?.type === 'monaco-ready') {
            console.log('Monaco editor is ready');
            this.ready.set(true);
            const win = this.editorIframe()!.nativeElement.contentWindow as any
            this.editorApi = win.api as MonacoIframeApi;
            if (this.initialValue()) {
                this.setValue(this.initialValue()!);
                Stdlib.let(this.language(), lang => this.setLanguage(lang));
                Stdlib.let(this.lineNos(), lineNos => this.setLineNumbers(lineNos));
                Stdlib.let(this.darkMode(), dark => this.setDark(dark));
                Stdlib.let(this.wordWrap(), wordWrap => this.setWordWrap(wordWrap));
                Stdlib.let(this.minimap(), minimap => this.setMinimap(minimap));
                Stdlib.let(this.fontSize(), fontSize => this.setFontSize(fontSize));
            }
        }
    }

    setValue(value: string) {
        this.editorApi?.setValue(value);
    }

    getValue(): string {
        return this.editorApi?.getValue() ?? '';
    }

    setLanguage(language: string) {
        this.editorApi?.setLanguage(language);
    }

    setFontSize(fontSize: number) {
        this.editorApi?.setFontSize(fontSize);
    }

    setLineNumbers(show: boolean) {
        this.editorApi?.setLineNumbers(show);
    }

    setDark(dark: boolean) {
        this.setTheme(dark ? 'vs-dark' : 'vs');
    }

    setTheme(theme: string) {
        this.editorApi?.setTheme(theme);
    }

    setWordWrap(wordWrap: boolean) {
        this.editorApi?.setWordWrap(wordWrap ? 'on' : 'off');
    }

    setMinimap(enabled: boolean) {
        this.editorApi?.setMinimap(enabled);
    }

    protected async onCopy() {
        await navigator.clipboard.writeText(this.getValue());
        // AppComponent.messageToast.show({ message: 'Copied to clipboard' });
    }

    static async openDialog(dynCompSvc: NgDynamicComponentsService, options: DialogContainerOptions<any>){
        return await dynCompSvc.createComponentDialog(MonacoEditorComponent, options);
    }
}
